import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  OrganizationPreview,
  PrivacyCenterChildOrganizationPreview,
} from '@main/access-control-types';
import { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import { buildUseLazyQuery, logger } from '@main/core-ui';
import { ImmutableUrl } from '@main/immutable-url';
import { AssetFile, endpoints, ParentPrivacyCenter } from '@main/pc-types';
import {
  createDhEncryptionKeys,
  createDhPayloadHelpers,
} from '@main/sm-browser';
import {
  isDiffieHellmanError,
  PurposeSchema,
  SombraDataSubjectAuthMethod,
} from '@main/sombra-types';

import {
  selectChildSessionProfiles,
  selectIsLoggedIn,
  selectRequiredPrivacyCenter,
} from '../containers/App/selectors';
import { useLogout } from './useLogout';
import {
  createStaticPrivacyCenterModuleUrl,
  useIsDraftPrivacyCenter,
} from './useStaticPrivacyCenterModule';

/**
 * Child organization with purposes
 */
export type ChildOrgWithPurposes = PrivacyCenterChildOrganizationPreview & {
  /** Purposes configured by the child organization */
  purposes?: PrivacyCenterTrackingPurposePreview[];
  /** Whether the purposes failed to load */
  failedToLoad?: boolean;
  /** User preferences from the child organization */
  consentPreferences?: PurposeSchema[];
};

const useLazyPurposes = buildUseLazyQuery(
  endpoints.privacyCenterPurposes,
  'PrivacyCenterPurposesForChildOrganization',
);

const useLazyGetPreferences = buildUseLazyQuery(
  endpoints.dataSubjectConsentPreferences,
  'PrivacyCenterGetPreferences',
);

/**
 * Grab metadata related to cross brand preference sharing
 *
 * @param skipPreferences - Whether to skip fetching preferences which requires a sombra session
 * @returns Metadata related to cross brand preference sharing
 */
export function useChildOrganizationPreferences(
  skipPreferences: boolean = false,
): {
  /** Whether the privacy center is in draft mode */
  isDraft: boolean;
  /** Parent organization */
  organization: OrganizationPreview;
  /** Parent privacy center */
  parentPrivacyCenter?: ParentPrivacyCenter;
  /** Child organizations */
  childOrganizations: ChildOrgWithPurposes[];
  /** Parent company logo */
  logo?: AssetFile;
} {
  const logout = useLogout();
  const { isDraft } = useIsDraftPrivacyCenter();
  const {
    childOrganizations,
    assets: { logo },
    organization,
    parentPrivacyCenter,
  } = useSelector(selectRequiredPrivacyCenter);
  const childSessionProfiles = useSelector(selectChildSessionProfiles);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [childOrganizationsWithPurposes, setChildOrganizationsWithPurposes] =
    useState(childOrganizations || []);
  const getPurposes = useLazyPurposes();
  const getPreferences = useLazyGetPreferences();

  // record a cache of each fetched file
  const [cachedResults, setCachedResults] = useState<
    Record<string, PrivacyCenterTrackingPurposePreview[]>
  >({});
  const [cachedPreferences, setCachedPreferences] = useState<
    Record<string, PurposeSchema[]>
  >({});

  useEffect(() => {
    Promise.all(
      (childOrganizations || []).map(
        async (childOrganization): Promise<ChildOrgWithPurposes> => {
          // leverage cache if exists
          if (
            cachedResults[childOrganization.id] &&
            cachedPreferences[childOrganization.id]
          ) {
            return {
              ...childOrganization,
              purposes: cachedResults[childOrganization.id],
              consentPreferences: cachedPreferences[childOrganization.id],
            };
          }

          let consentPreferences: PurposeSchema[] = [];

          if (!skipPreferences) {
            const childSessionProfile = (childSessionProfiles ?? []).find(
              ({ organizationId }) => organizationId === childOrganization.id,
            );

            if (!childSessionProfile) {
              throw new Error(
                `Missing auth secret for child organization: ${childOrganization.id}`,
              );
            }

            const [createDhPayload] = createDhPayloadHelpers({
              authMethod: SombraDataSubjectAuthMethod.Session,
              authSecret: childSessionProfile.authSecret,
              dhEncryptionKeys: createDhEncryptionKeys(
                childOrganization.sombraPublicKeyECDH!,
              ),
            });

            if (isLoggedIn && createDhPayload) {
              const { data: preferencesResponse, error } = await getPreferences(
                {
                  input: {
                    partition:
                      childOrganization.airgapBundleId as unknown as string,
                    identifiers: [childSessionProfile.coreIdentifier.value],
                  },
                  lookup: { url: childOrganization.deployedPrivacyCenterUrl },
                  dhEncrypted: createDhPayload({}),
                },
              );

              // handle logout if session expires
              if (error?.message && isDiffieHellmanError(error.message)) {
                logout();
              }

              consentPreferences =
                preferencesResponse.nodes?.[0]?.purposes || [];
            }
          }

          // fetch from GraphQL endpoints if draft version
          if (isDraft) {
            const { data, errors } = await getPurposes({
              lookup: { url: childOrganization.deployedPrivacyCenterUrl },
            });
            if (errors) {
              logger.error(JSON.stringify(errors));
              return {
                ...childOrganization,
                purposes: [] as PrivacyCenterTrackingPurposePreview[],
                failedToLoad: true,
                consentPreferences,
              };
            }

            return {
              ...childOrganization,
              purposes: data,
              failedToLoad: false,
              consentPreferences,
            };
          }

          // fetch from cached s3 files
          const url = createStaticPrivacyCenterModuleUrl({
            module: 'privacyCenterPurposes',
            host: new ImmutableUrl(childOrganization.deployedPrivacyCenterUrl)
              .host,
          });
          const data = await fetch(url).then(async (res) => {
            // status code is 2XX
            let jsonBody: any;
            let isError = !res.ok;
            try {
              jsonBody = await res.json();
            } catch (e) {
              jsonBody = e.message;
              isError = true;
            }
            if (!isError) {
              return jsonBody;
            }
            return null;
          });
          return {
            ...childOrganization,
            purposes: data || ([] as PrivacyCenterTrackingPurposePreview[]),
            failedToLoad: !data,
            consentPreferences,
          };
        },
      ),
    ).then((result) => {
      setChildOrganizationsWithPurposes(result);
      const newCachedResults = { ...cachedResults };
      const newCachedPreferences = { ...cachedPreferences };
      result.forEach((childOrganization) => {
        if (!childOrganization.failedToLoad) {
          newCachedResults[childOrganization.id] =
            childOrganization.purposes || [];
          newCachedPreferences[childOrganization.id] =
            childOrganization.consentPreferences || [];
        }
      });
      setCachedResults(newCachedResults);
      setCachedPreferences(newCachedPreferences);
    });
    // no need to include cachedResults in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft, childOrganizations]);

  return {
    isDraft,
    organization,
    logo,
    parentPrivacyCenter,
    childOrganizations: childOrganizationsWithPurposes,
  };
}
